import React,{useState} from 'react';
import {motion,AnimatePresence,useReducedMotion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import {useAuth} from '../../contexts/AuthContext';
import {useTheme} from '../../contexts/ThemeContext';

const {
  FiMail,FiLock,FiEye,FiEyeOff,FiLogIn,FiUserPlus,FiAlertCircle,FiUser,FiSearch,FiFolder,FiCloud,FiLoader
}=FiIcons;

const LoginForm=()=> {
  const [isLogin,setIsLogin]=useState(true);
  const [showPassword,setShowPassword]=useState(false);
  const [formData,setFormData]=useState({email:'',password:'',passwordConfirm:'',name:''});
  const [loading,setLoading]=useState(false);
  const {signIn,signUp,error}=useAuth();
  const {theme}=useTheme();
  const reduceMotion=useReducedMotion();

  const motionTransition=reduceMotion ? {duration:0.01} : {duration:0.34,ease:[0.16,1,0.3,1]};
  const inputClass='w-full min-h-[54px] pl-12 pr-4 py-3.5 rounded-xl border text-base font-semibold outline-none transition-[border-color,box-shadow,background-color] duration-200 disabled:opacity-60 '+
    (theme==='dark'
      ? 'bg-zinc-900/80 border-zinc-700/90 text-white placeholder:text-zinc-600 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10'
      : 'bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10');
  const labelClass='text-[11px] font-black uppercase tracking-[0.16em] text-slate-500 ml-0.5';

  const handleSubmit=async (event)=> {
    event.preventDefault();
    if(loading) return;
    setLoading(true);
    try {
      const email=formData.email.trim();
      if(isLogin) await signIn(email,formData.password);
      else await signUp(email,formData.password,formData.passwordConfirm,formData.name.trim());
    } catch (submitError) {
      console.error(submitError);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange=(event)=> {
    const {name,value}=event.target;
    setFormData(previous=> ({...previous,[name]:value}));
  };

  const switchMode=(nextLogin)=> {
    if(nextLogin===isLogin || loading) return;
    setIsLogin(nextLogin);
    setShowPassword(false);
  };

  const patternStyle={
    backgroundImage: theme==='dark'
      ? 'radial-gradient(circle at 1px 1px, rgba(99,102,241,0.18) 1px, transparent 1.25px)'
      : 'radial-gradient(circle at 1px 1px, rgba(59,130,246,0.12) 1px, transparent 1.25px)',
    backgroundSize:'26px 26px'
  };

  const panelPatternStyle={
    backgroundImage:'linear-gradient(rgba(255,255,255,0.055) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.055) 1px, transparent 1px)',
    backgroundSize:'44px 44px'
  };

  return (
    <div
      className={'relative min-h-[100dvh] overflow-y-auto px-3 sm:px-5 lg:px-8 pt-[max(0.75rem,env(safe-area-inset-top,0px))] pb-[max(0.75rem,calc(env(safe-area-inset-bottom,0px)+0.5rem))] flex items-center justify-center '+(theme==='dark' ? 'bg-[#07101f]' : 'bg-[#eef4ff]')}
      style={patternStyle}
    >
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-blue-500/[0.08] to-transparent" />
        <div className="absolute -top-32 -right-24 w-96 h-96 rounded-full bg-indigo-500/[0.12]" />
        <div className="absolute -bottom-40 -left-28 w-[30rem] h-[30rem] rounded-full bg-blue-500/[0.08]" />
      </div>

      <motion.main
        initial={{opacity:0,y:reduceMotion ? 0 : 14,scale:reduceMotion ? 1 : 0.992}}
        animate={{opacity:1,y:0,scale:1}}
        transition={motionTransition}
        className={'relative z-10 w-full max-w-6xl overflow-hidden rounded-[2rem] border shadow-[0_32px_90px_-34px_rgba(15,23,42,0.45)] lg:grid lg:grid-cols-[1.05fr_0.95fr] '+(theme==='dark' ? 'bg-zinc-950 border-white/[0.08]' : 'bg-white border-white/90')}
      >
        <section className="relative hidden lg:flex min-h-[680px] flex-col overflow-hidden bg-[#0b1730] text-white p-10 xl:p-12">
          <div className="absolute inset-0 opacity-80" style={panelPatternStyle} aria-hidden="true" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_18%,rgba(59,130,246,0.34),transparent_32%),radial-gradient(circle_at_82%_72%,rgba(99,102,241,0.30),transparent_35%)]" aria-hidden="true" />
          <div className="absolute right-[-12%] top-[18%] w-72 h-72 rounded-full border border-white/[0.08]" aria-hidden="true" />
          <div className="absolute right-[-6%] top-[24%] w-52 h-52 rounded-full border border-white/[0.07]" aria-hidden="true" />

          <div className="relative z-10 flex items-center gap-3">
            <img src="/APP-ICON.svg" alt="" className="w-12 h-12 rounded-2xl shadow-xl shadow-blue-950/30" />
            <div>
              <div className="font-black text-[17px] leading-tight tracking-[-0.02em]">Bookmarks Manager Pro</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-blue-200/70 font-bold mt-1">Personal library workspace</div>
            </div>
          </div>

          <div className="relative z-10 my-auto max-w-lg py-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-blue-300/20 bg-blue-400/[0.08] text-blue-100 text-[11px] font-black uppercase tracking-[0.15em]">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_0_4px_rgba(52,211,153,0.09)]" />
              Your library, always ready
            </div>
            <h1 className="mt-6 text-[48px] xl:text-[54px] leading-[1.02] tracking-[-0.045em] font-black">
              Save less time searching. Find what matters faster.
            </h1>
            <p className="mt-5 max-w-md text-[15px] leading-7 text-slate-300">
              Keep applications, references and important links organised in one calm workspace across your devices.
            </p>

            <div className="mt-9 grid gap-3">
              {[
                [FiCloud,'Cloud library','Keep your bookmark collection available across devices.'],
                [FiSearch,'Fast search','Find bookmarks, folders and saved searches without digging.'],
                [FiFolder,'Structured folders','Keep work, clients and personal resources clearly organised.']
              ].map(([icon,title,text])=> (
                <div key={title} className="flex items-start gap-3 rounded-2xl border border-white/[0.08] bg-white/[0.045] p-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/15 text-blue-300 flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={icon} className="w-[18px] h-[18px]" />
                  </div>
                  <div>
                    <div className="text-sm font-black">{title}</div>
                    <div className="text-xs leading-5 text-slate-400 mt-1">{text}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative z-10 flex items-center justify-between gap-5 text-[11px] text-slate-400">
            <span>Secure cloud access</span>
            <span className="text-slate-500">Bookmarks Manager Pro</span>
          </div>
        </section>

        <section className={'relative flex items-center justify-center min-h-[calc(100dvh-1.5rem)] lg:min-h-[680px] px-5 py-7 sm:px-8 sm:py-10 lg:px-10 xl:px-14 '+(theme==='dark' ? 'bg-zinc-950' : 'bg-white')}>
          <div className="w-full max-w-md">
            <div className="lg:hidden flex items-center gap-3 mb-8">
              <img src="/APP-ICON.svg" alt="" className="w-11 h-11 rounded-2xl shadow-lg shadow-blue-500/15" />
              <div>
                <div className="text-[15px] font-black tracking-[-0.02em]">Bookmarks Manager Pro</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold mt-0.5">Personal library workspace</div>
              </div>
            </div>

            <div className={'grid grid-cols-2 gap-1 p-1 rounded-xl mb-8 '+(theme==='dark' ? 'bg-zinc-900 border border-zinc-800' : 'bg-slate-100 border border-slate-200/70')} role="tablist" aria-label="Account access">
              <button
                type="button"
                role="tab"
                aria-selected={isLogin}
                onClick={()=> switchMode(true)}
                disabled={loading}
                className={'min-h-11 rounded-lg text-sm font-black transition-[background-color,color,box-shadow] duration-200 disabled:opacity-50 '+(isLogin ? (theme==='dark' ? 'bg-zinc-800 text-white shadow-sm' : 'bg-white text-slate-900 shadow-sm') : 'text-slate-500 hover:text-blue-600')}
              >
                Sign in
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={!isLogin}
                onClick={()=> switchMode(false)}
                disabled={loading}
                className={'min-h-11 rounded-lg text-sm font-black transition-[background-color,color,box-shadow] duration-200 disabled:opacity-50 '+(!isLogin ? (theme==='dark' ? 'bg-zinc-800 text-white shadow-sm' : 'bg-white text-slate-900 shadow-sm') : 'text-slate-500 hover:text-blue-600')}
              >
                Create account
              </button>
            </div>

            <div className="mb-7">
              <div className="text-[11px] font-black uppercase tracking-[0.18em] text-blue-600 dark:text-blue-400">
                {isLogin ? 'Welcome back' : 'Start your library'}
              </div>
              <h2 className="mt-2 text-3xl sm:text-[34px] leading-tight font-black tracking-[-0.035em]">
                {isLogin ? 'Sign in to your workspace' : 'Create your account'}
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-500">
                {isLogin ? 'Continue to your organised bookmark library.' : 'Create an account and start building your bookmark library.'}
              </p>
            </div>

            <AnimatePresence mode="wait" initial={false}>
              {error && (
                <motion.div
                  key={error}
                  initial={{opacity:0,y:reduceMotion ? 0 : -6}}
                  animate={{opacity:1,y:0}}
                  exit={{opacity:0}}
                  transition={motionTransition}
                  className="mb-5 p-3.5 rounded-xl bg-red-500/[0.08] border border-red-500/20 text-red-600 dark:text-red-400 text-sm font-semibold flex items-start gap-3"
                  role="alert"
                >
                  <SafeIcon icon={FiAlertCircle} className="w-[18px] h-[18px] flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </motion.div>
              )}
            </AnimatePresence>

            <form onSubmit={handleSubmit} className="space-y-4" aria-busy={loading}>
              <AnimatePresence initial={false}>
                {!isLogin && (
                  <motion.div
                    initial={{opacity:0,height:0}}
                    animate={{opacity:1,height:'auto'}}
                    exit={{opacity:0,height:0}}
                    transition={motionTransition}
                    className="overflow-hidden"
                  >
                    <div className="space-y-2 pb-0.5">
                      <label htmlFor="login-name" className={labelClass}>Full Name</label>
                      <div className="relative group">
                        <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors w-5 h-5" />
                        <input
                          id="login-name"
                          type="text"
                          name="name"
                          required={!isLogin}
                          disabled={loading}
                          autoComplete="name"
                          enterKeyHint="next"
                          value={formData.name}
                          onChange={handleInputChange}
                          className={inputClass}
                          placeholder="Your name"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="space-y-2">
                <label htmlFor="login-email" className={labelClass}>Email</label>
                <div className="relative group">
                  <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors w-5 h-5" />
                  <input
                    id="login-email"
                    type="email"
                    name="email"
                    required
                    disabled={loading}
                    autoComplete="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    inputMode="email"
                    enterKeyHint="next"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="name@company.com"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="login-password" className={labelClass}>Password</label>
                <div className="relative group">
                  <SafeIcon icon={FiLock} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors w-5 h-5" />
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    required
                    disabled={loading}
                    autoComplete={isLogin ? 'current-password' : 'new-password'}
                    enterKeyHint={isLogin ? 'go' : 'next'}
                    value={formData.password}
                    onChange={handleInputChange}
                    className={inputClass+' pr-14'}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={()=> setShowPassword(!showPassword)}
                    disabled={loading}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-500/[0.06] transition-[background-color,color] disabled:opacity-50"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <AnimatePresence initial={false}>
                {!isLogin && (
                  <motion.div
                    initial={{opacity:0,height:0}}
                    animate={{opacity:1,height:'auto'}}
                    exit={{opacity:0,height:0}}
                    transition={motionTransition}
                    className="overflow-hidden"
                  >
                    <div className="space-y-2 pb-0.5">
                      <label htmlFor="login-password-confirm" className={labelClass}>Confirm Password</label>
                      <div className="relative group">
                        <SafeIcon icon={FiLock} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors w-5 h-5" />
                        <input
                          id="login-password-confirm"
                          type={showPassword ? 'text' : 'password'}
                          name="passwordConfirm"
                          required={!isLogin}
                          disabled={loading}
                          autoComplete="new-password"
                          enterKeyHint="go"
                          value={formData.passwordConfirm}
                          onChange={handleInputChange}
                          className={inputClass}
                          placeholder="Repeat password"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <button
                type="submit"
                disabled={loading}
                className="mt-2 w-full min-h-[54px] px-4 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-black text-sm flex items-center justify-center gap-2.5 shadow-[0_14px_28px_-16px_rgba(37,99,235,0.9)] hover:from-blue-700 hover:to-indigo-700 active:from-blue-800 active:to-indigo-800 transition-[background-image,opacity,transform] disabled:opacity-60 disabled:cursor-wait"
              >
                <SafeIcon icon={loading ? FiLoader : (isLogin ? FiLogIn : FiUserPlus)} className={'w-[18px] h-[18px] '+(loading ? 'animate-spin' : '')} />
                <span>{loading ? (isLogin ? 'Signing in…' : 'Creating account…') : (isLogin ? 'Sign in' : 'Create account')}</span>
              </button>
            </form>

            <div className="mt-6 pt-5 border-t border-slate-200/80 dark:border-zinc-800 flex items-center justify-center gap-2 text-xs text-slate-500">
              <SafeIcon icon={FiLock} className="w-3.5 h-3.5" />
              <span>Secure cloud access to your bookmark library</span>
            </div>
          </div>
        </section>
      </motion.main>
    </div>
  );
};

export default LoginForm;